import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    useDataTable,
    MoreFiltersMenu,
    useDebounce,
    type DataTableButtonAction,
    type MoreMenuCategory
} from '@odo/shared-ui';
import ConversationPanel from '../components/messages/ConversationPanel';
import { CONVERSATION_STATUS_OPTIONS } from '../components/conversations/ConversationStatusBadge';
import { buildConversationColumns } from '../components/conversations/conversationColumns';
import FilterDropdown from '../components/filtering/FilterDropdown';
import { practiceOptionsFromList } from '../utils/practiceFilterOptions';
import { usePracticeOptions } from '../hooks/useBilling';
import { useServerConversations } from '../hooks/useServerConversations';
import { useInfiniteConversations } from '../hooks/useInfiniteConversations';
import { useConversationActions } from '../hooks/useConversationActions';
import { useMultiSelect } from '../hooks/useMultiSelect';
import { useFilterOptions } from '../hooks/useFilterOptions';
import { useSidebarCounts } from '../hooks/useSidebarCounts';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageHeader } from '../providers/PageHeaderProvider';
import { useUser } from '../providers/UserProvider';
import { useToast } from '../providers/ToastProvider';
import { useConversationsDeepLink } from '../hooks/useConversationsDeepLink';
import { useAssigneeFilter } from '../hooks/useAssigneeFilter';
import {
    conversationRowKey,
    UNASSIGNED_CONVERSATION_FILTER,
    type ConversationRow,
    type ConversationSortBy,
    type ConversationStatus
} from '../services/conversations';

const ROW_HEIGHT = 64;
const MIN_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 50;
// Scroll mode's fixed chunk, mirroring useDataTable's infiniteLimit: a
// viewport-fit size is meaningless for a list that grows past the viewport.
const INFINITE_CHUNK_SIZE = 50;
const VIEW_MODE_KEY = 'conversationsTableViewMode';
// Unread rows are tinted per thread: amber for the patient chat, brand teal for
// internal. The accent is DataTable's leading-edge strip, the tint the row class.
const UNREAD_ACCENT: Record<ConversationStatus, string> = {
    external: '#facc15',
    internal: '#007E87'
};
const UNREAD_ROW_TINT: Record<ConversationStatus, string> = {
    external: 'text-sm text-gray-900 bg-yellow-50/40 font-medium hover:bg-yellow-50/70',
    internal: 'text-sm text-gray-900 bg-[#007E87]/[0.07] font-medium hover:bg-[#007E87]/[0.12]'
};

const isConversationStatus = (value: string): value is ConversationStatus =>
    value === 'internal' || value === 'external';

type StatusFilter = 'unread' | 'seen' | 'all';

// Type alias, not interface: useDataTable's filter-map constraint requires it.
type ConversationFilters = {
    practice: string[];
    team: string[];
    assignee: string[];
    status: ConversationStatus[];
};

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
    { label: 'Unread', value: 'unread' },
    { label: 'All', value: 'all' },
    { label: 'Treated', value: 'seen' }
];

const Conversations: React.FC = () => {
    usePageTitle('Conversations');
    const { setTitle, setSubtitle } = usePageHeader();
    const { conversations: unreadCounts } = useSidebarCounts();
    const externalUnread = unreadCounts?.external;
    const internalUnread = unreadCounts?.internal;
    useEffect(() => {
        setTitle('Conversations');
        setSubtitle(
            externalUnread === undefined || internalUnread === undefined
                ? null
                : `${externalUnread.toLocaleString()} external unread · ${internalUnread.toLocaleString()} internal unread`
        );
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [externalUnread, internalUnread, setTitle, setSubtitle]);
    const { user } = useUser();
    const { showToast } = useToast();

    // Status tab + search stay page-level: the tab is a bespoke dropdown
    // persisting a raw string, and search needs debouncing.
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
        const stored = sessionStorage.getItem('fv_conv_statusFilter');
        return stored === 'unread' || stored === 'seen' || stored === 'all' ? stored : 'unread';
    });
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const statusFilterRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 300);

    const table = useDataTable<ConversationSortBy, ConversationFilters>({
        defaultSort: { by: 'date', order: 'desc' },
        defaultLimit: MIN_PAGE_SIZE,
        // Constant on purpose: the page owns view mode (localStorage); the
        // hook only manages the paged limit. Do NOT wire table.setMode here.
        mode: 'paged',
        defaultFilters: { practice: [], team: [], assignee: [], status: [] },
        storageKey: 'fv_conv_table',
        filterStorageKeys: {
            // Values are practice IDS (names aren't unique) - the key differs
            // from the retired name-based one so stale name selections can't
            // be restored as ids.
            practice: 'fv_conv_practiceIdFilter',
            team: 'fv_conv_teamFilter',
            assignee: 'fv_conv_assigneeFilter',
            // Distinct from `fv_conv_statusFilter`, which holds the unread/all/
            // treated tab - this one is the internal/external thread filter.
            status: 'fv_conv_conversationStatusFilter'
        },
        defaultOrderFor: (by) => (by === 'name' ? 'asc' : 'desc')
    });
    const { setPage, setLimit, setFilter } = table;

    // View mode is a durable preference (like the work queue's) - it stays
    // page-owned in localStorage rather than in the session-scoped table blob.
    const [viewMode, setViewMode] = useState<'paged' | 'scroll'>(() => {
        const stored = localStorage.getItem(VIEW_MODE_KEY);
        return stored === 'paged' ? 'paged' : 'scroll';
    });
    const handleViewModeChange = useCallback(
        (mode: 'paged' | 'scroll') => {
            setViewMode(mode);
            localStorage.setItem(VIEW_MODE_KEY, mode);
            if (mode === 'paged') setPage(1);
        },
        [setPage]
    );

    const [selectedPatient, setSelectedPatient] = useState<ConversationRow | null>(null);

    const { filterOptions } = useFilterOptions();
    const setAssigneeFilter = useCallback(
        (values: string[]) => setFilter('assignee', values),
        [setFilter]
    );
    const { options: assigneeOptions, apiValues: resolvedAssigneeEmails } = useAssigneeFilter(
        table.filters.assignee,
        UNASSIGNED_CONVERSATION_FILTER,
        setAssigneeFilter
    );

    // Shared filter params. Paged uses the measured viewport fit; scroll takes
    // the fixed chunk, so a resize can't rewrite its key and drop the list.
    const filterParams = {
        filter: statusFilter,
        limit: viewMode === 'scroll' ? INFINITE_CHUNK_SIZE : table.limit,
        sort: table.sort,
        order: table.order,
        team: table.filters.team.length > 0 ? table.filters.team : undefined,
        assignee: resolvedAssigneeEmails.length > 0 ? resolvedAssigneeEmails : undefined,
        practiceIds: table.filters.practice.length > 0 ? table.filters.practice : undefined,
        status: table.filters.status.length > 0 ? table.filters.status : undefined,
        search: debouncedSearch || undefined
    };

    // Only the active mode fetches
    const paged = useServerConversations({
        ...filterParams,
        page: table.page,
        enabled: viewMode === 'paged'
    });
    const infinite = useInfiniteConversations({ ...filterParams, enabled: viewMode === 'scroll' });

    // Unified data based on view mode
    const conversations = viewMode === 'scroll' ? infinite.conversations : paged.conversations;
    const total = viewMode === 'scroll' ? infinite.total : paged.meta.total;
    const totalPages = viewMode === 'scroll' ? 1 : paged.meta.totalPages;
    const isLoading = viewMode === 'scroll' ? infinite.isLoading : paged.isLoading;
    const isFetching = viewMode === 'scroll' ? infinite.isFetching : paged.isFetching;
    const isError = viewMode === 'scroll' ? infinite.isError : paged.isError;
    const refresh = viewMode === 'scroll' ? infinite.refresh : paged.refresh;

    const { hasNextPage, isFetchingNextPage, fetchNextPage } = infinite;
    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Actions (mark as seen/unread, team change, assign/unassign)
    const {
        markingSeenKey,
        markingUnreadKey,
        handleMarkAsSeen,
        handleMarkAsUnread,
        handleTeamChange,
        handleAssign,
        handleUnassign
    } = useConversationActions(refresh, showToast);

    // Id-keyed options from GET /v2/practices/options, doctor as sub-label
    // and keyword - two practices sharing a name stay two distinct options.
    const { practices } = usePracticeOptions();
    const practiceOptions = useMemo(() => practiceOptionsFromList(practices), [practices]);

    // Less-used filters live behind the "More" button (mirrors the work queue).
    const moreCategories: MoreMenuCategory[] = [
        {
            key: 'team',
            label: 'Team',
            options: filterOptions.teams,
            selectedOptions: table.filters.team,
            onChange: (v) => table.setFilter('team', v)
        },
        {
            key: 'practice',
            label: 'Practice',
            options: practiceOptions,
            selectedOptions: table.filters.practice,
            onChange: (v) => table.setFilter('practice', v)
        },
        {
            key: 'status',
            label: 'Visibility',
            options: CONVERSATION_STATUS_OPTIONS,
            selectedOptions: table.filters.status,
            onChange: (v) => table.setFilter('status', v.filter(isConversationStatus))
        }
    ];
    const moreActiveCount = moreCategories.filter((c) => c.selectedOptions.length > 0).length;

    // Viewport-based page size: the scroll region's height (DataTable ref)
    // divided by the row height, clamped - the chunk size in both modes.
    const scrollRef = useRef<HTMLDivElement>(null);
    const lastPageSizeRef = useRef<number | null>(null);
    // Returns whether it actually measured, so a caller that only gets one
    // attempt doesn't spend it on a null ref.
    const measurePageSize = useCallback((): boolean => {
        if (!scrollRef.current) return false;
        const usable = scrollRef.current.clientHeight;
        const next = Math.min(
            MAX_PAGE_SIZE,
            Math.max(MIN_PAGE_SIZE, Math.floor(usable / ROW_HEIGHT))
        );
        if (next === lastPageSizeRef.current) return true;
        lastPageSizeRef.current = next;
        setLimit(next);
        return true;
    }, [setLimit]);

    useLayoutEffect(() => {
        measurePageSize();
        const onResize = () => measurePageSize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [measurePageSize]);
    // Once when real rows first land, never per row-count change: `limit`
    // feeds the query key, so measuring off a count that depends on the limit
    // oscillates forever (React #185 - see useViewportPageSize.remeasureOnce).
    const measuredRealRowsRef = useRef(false);
    useLayoutEffect(() => {
        if (measuredRealRowsRef.current || conversations.length === 0) return;
        measuredRealRowsRef.current = measurePageSize();
    }, [measurePageSize, conversations.length]);

    // Click-outside for status dropdown
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (statusFilterRef.current && !statusFilterRef.current.contains(e.target as Node))
                setStatusFilterOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Reset page when the page-level filters change (table-owned filters
    // already reset via the hook).
    useEffect(() => {
        setPage(1);
    }, [statusFilter, debouncedSearch, setPage]);

    // Persist the status tab (raw string - legacy format)
    useEffect(() => {
        sessionStorage.setItem('fv_conv_statusFilter', statusFilter);
    }, [statusFilter]);

    const selectPatient = useCallback((row: ConversationRow) => {
        setSelectedPatient(row);
    }, []);
    const setTeamFilter = useCallback((teams: string[]) => setFilter('team', teams), [setFilter]);
    const clearScopeFilters = useCallback(() => {
        setFilter('practice', []);
        setFilter('status', []);
    }, [setFilter]);
    const reportMissingConversation = useCallback(
        (missing: boolean) =>
            showToast(
                missing
                    ? 'That conversation is no longer available.'
                    : "Couldn't load that conversation. Try again.",
                'error'
            ),
        [showToast]
    );
    useConversationsDeepLink({
        selectedPatient,
        onSelect: selectPatient,
        onUnavailable: reportMissingConversation,
        setAssignee: setAssigneeFilter,
        setTeam: setTeamFilter,
        clearScope: clearScopeFilters,
        setStatus: setStatusFilter
    });

    const handleClosePanel = useCallback(() => {
        setSelectedPatient(null);
    }, []);
    // The spread keeps the other thread's row fields; the panel only reads the
    // patient-level ones (id, name, practice), the URL sync only id and status.
    const handlePanelChatTypeChange = useCallback((status: ConversationStatus) => {
        setSelectedPatient((prev) => prev && { ...prev, status });
    }, []);

    // Multi-select and bulk actions
    const {
        selectMode,
        setSelectMode,
        selectedKeys,
        isBulkProcessing,
        exitSelectMode,
        toggleRow,
        toggleSelectAll,
        selectionType,
        handleBulkMarkAsDone,
        handleBulkMarkAsUnread
    } = useMultiSelect(conversations, conversations, refresh, showToast, [
        statusFilter,
        debouncedSearch,
        table.filters.practice,
        table.filters.team,
        table.filters.assignee,
        table.filters.status,
        table.page,
        table.sort,
        table.order
    ]);

    const isRowSelected = useCallback(
        (row: ConversationRow): boolean => selectMode && selectedKeys.has(conversationRowKey(row)),
        [selectMode, selectedKeys]
    );

    const hasActiveFilters =
        debouncedSearch.length > 0 ||
        table.filters.practice.length > 0 ||
        table.filters.team.length > 0 ||
        table.filters.assignee.length > 0 ||
        table.filters.status.length > 0;

    const currentUserEmail = user?.email ?? '';
    const rowCount = conversations.length;
    const columns = useMemo(
        () =>
            buildConversationColumns({
                currentUserEmail,
                rowCount,
                markingSeenKey,
                markingUnreadKey,
                onMarkSeen: handleMarkAsSeen,
                onMarkUnread: handleMarkAsUnread,
                onTeamChange: handleTeamChange,
                onAssign: handleAssign,
                onUnassign: handleUnassign
            }),
        [
            currentUserEmail,
            rowCount,
            markingSeenKey,
            markingUnreadKey,
            handleMarkAsSeen,
            handleMarkAsUnread,
            handleTeamChange,
            handleAssign,
            handleUnassign
        ]
    );

    // Selection-bar content, recomputed per render from what is selected.
    // Homogeneous selections get their bulk action; mixed ones get a warning.
    const selectionNotice =
        selectedKeys.size === 0 ? (
            <span className="text-sm text-gray-500">
                Select conversations to perform bulk actions
            </span>
        ) : selectionType === 'mixed' ? (
            <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                Select only unread or only treated conversations
            </span>
        ) : undefined;

    const bulkSpinner = (colorClass: string) => (
        <div
            className={`h-3.5 w-3.5 border-2 ${colorClass} border-t-transparent rounded-full animate-spin`}
        />
    );
    const selectionActions: DataTableButtonAction[] =
        selectedKeys.size === 0 || selectionType === 'mixed'
            ? []
            : selectionType === 'unread'
              ? [
                    {
                        key: 'bulk-done',
                        label: 'Mark selected as done',
                        onClick: handleBulkMarkAsDone,
                        disabled: isBulkProcessing,
                        icon: isBulkProcessing ? bulkSpinner('border-green-500') : undefined,
                        className:
                            'flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 hover:bg-green-100 transition-colors disabled:opacity-50'
                    }
                ]
              : [
                    {
                        key: 'bulk-unread',
                        label: 'Mark selected as unread',
                        onClick: handleBulkMarkAsUnread,
                        disabled: isBulkProcessing,
                        icon: isBulkProcessing ? bulkSpinner('border-orange-400') : undefined,
                        className:
                            'flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 hover:bg-orange-100 transition-colors disabled:opacity-50'
                    }
                ];

    // In-card bars: background sync + result count (the selection bar is
    // DataTable's, driven by the `selection` prop).
    const cardHeader = (
        <>
            {isFetching && !isLoading && (
                <div className="px-6 py-2.5 border-b border-gray-200 bg-gradient-to-r from-blue-50/50 to-white flex items-center gap-2.5">
                    <div className="animate-spin h-3.5 w-3.5 border-2 border-blue-400 border-t-transparent rounded-full" />
                    <p className="text-sm text-gray-600 font-medium">Syncing latest data...</p>
                </div>
            )}
            {hasActiveFilters && (
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <p className="text-sm text-gray-600">
                        {total === 0
                            ? `No conversations found${debouncedSearch ? ` matching "${debouncedSearch}"` : ''}`
                            : `Found ${total} conversation${total === 1 ? '' : 's'}${debouncedSearch ? ` matching "${debouncedSearch}"` : ''}`}
                    </p>
                </div>
            )}
        </>
    );

    const emptyState = isError ? (
        <div className="flex flex-col items-center justify-center gap-3">
            <p className="text-sm text-red-500 font-medium">Failed to load conversations</p>
            <button
                onClick={() => refresh()}
                className="text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 transition-colors"
            >
                Retry
            </button>
        </div>
    ) : hasActiveFilters ? (
        <p className="text-sm text-gray-500 text-center">
            No conversations found matching your filters.
        </p>
    ) : (
        <div className="flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center">
                <svg
                    className="w-8 h-8 text-yellow-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
            </div>
            <p className="text-lg font-semibold text-gray-800">You&apos;re all caught up!</p>
        </div>
    );

    return (
        <div className="flex flex-col h-full w-full">
            {/* Action bar */}
            <div className="flex flex-wrap items-center justify-between gap-y-3 w-full px-8 mt-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center w-[340px] h-11 bg-white border border-gray-200 rounded-xl px-4 flex-shrink-0 min-w-0">
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
                        <input
                            type="text"
                            placeholder="Search by patient name"
                            className="w-full min-w-0 bg-transparent outline-none text-gray-700 placeholder-gray-400 text-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600"
                                aria-label="Clear search"
                            >
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                        className={`inline-flex items-center gap-2 rounded-full border-2 shadow-sm px-4 py-2 text-sm font-medium transition-all duration-300 focus:outline-none ${
                            selectMode
                                ? 'bg-gray-800 text-white border-gray-800'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {selectMode && <XMarkIcon className="w-4 h-4" />}
                        {selectMode ? 'Cancel' : 'Select'}
                    </button>
                    {/* Status filter dropdown */}
                    <div className="relative inline-block text-left" ref={statusFilterRef}>
                        <button
                            type="button"
                            onClick={() => setStatusFilterOpen((prev) => !prev)}
                            className={`inline-flex justify-between items-center rounded-full border-2 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-300 ${
                                statusFilterOpen ? 'border-gray-700' : 'border-gray-300'
                            }`}
                            style={{ minWidth: '140px' }}
                        >
                            <span>{STATUS_TABS.find((t) => t.value === statusFilter)?.label}</span>
                            <ChevronDownIcon
                                className={`h-5 w-5 ml-2 transition-transform duration-300 ${statusFilterOpen ? 'rotate-180' : ''}`}
                            />
                        </button>
                        <div
                            className={`absolute right-0 mt-2 w-40 z-20 bg-white shadow-lg rounded-2xl transition-all duration-300 ease-in-out overflow-hidden ring-2 ${
                                statusFilterOpen ? 'ring-gray-700' : 'ring-transparent'
                            }`}
                            style={{
                                maxHeight: statusFilterOpen ? '12rem' : '0',
                                opacity: statusFilterOpen ? 1 : 0
                            }}
                        >
                            <div className="py-1">
                                {STATUS_TABS.map((tab) => (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        onClick={() => {
                                            setStatusFilter(tab.value);
                                            setPage(1);
                                            setStatusFilterOpen(false);
                                        }}
                                        className={`flex items-center w-full px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                                            statusFilter === tab.value
                                                ? 'bg-gray-100 text-gray-900 font-semibold'
                                                : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span
                                            className={`w-2 h-2 rounded-full mr-3 ${statusFilter === tab.value ? 'bg-gray-800' : 'bg-transparent'}`}
                                        />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <FilterDropdown
                        title="Assignee"
                        options={assigneeOptions}
                        selectedOptions={table.filters.assignee}
                        onChange={(v) => table.setFilter('assignee', v)}
                        align="right"
                    />
                    <MoreFiltersMenu
                        categories={moreCategories}
                        allCategories={moreCategories}
                        hiddenActiveCount={moreActiveCount}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 min-h-0 flex flex-col w-full">
                <div className="px-8 py-4 w-full h-full flex flex-col">
                    <DataTable
                        label="Conversations"
                        columns={columns}
                        rows={conversations}
                        getRowKey={conversationRowKey}
                        rowHeightClassName="h-16"
                        cardHeader={cardHeader}
                        sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                        mode={viewMode === 'scroll' ? 'infinite' : 'paged'}
                        onModeChange={(m) =>
                            handleViewModeChange(m === 'infinite' ? 'scroll' : 'paged')
                        }
                        onLoadMore={handleLoadMore}
                        hasMore={hasNextPage}
                        isLoadingMore={isFetchingNextPage}
                        pagination={
                            total > 0
                                ? { page: table.page, totalPages, total, onPage: setPage }
                                : undefined
                        }
                        selection={{
                            enabled: selectMode,
                            selectedKeys,
                            onToggleRow: toggleRow,
                            onToggleAll: () => toggleSelectAll(),
                            selectedRowClassName: 'bg-gray-100/80 ring-1 ring-inset ring-gray-300',
                            actions: selectionActions,
                            actionsNotice: selectionNotice
                        }}
                        rowAction={{
                            mode: 'click',
                            onActivate: (row) => setSelectedPatient(row)
                        }}
                        // Selected rows suppress the unread accent and tint
                        // (legacy behavior): the selection styling wins alone.
                        rowAccent={(row) =>
                            row.unread && !isRowSelected(row)
                                ? UNREAD_ACCENT[row.status]
                                : undefined
                        }
                        rowClassName={(row) => {
                            if (isRowSelected(row)) return 'text-sm text-gray-900';
                            return row.unread
                                ? UNREAD_ROW_TINT[row.status]
                                : 'text-sm text-gray-400 bg-gray-50/50 hover:bg-gray-50';
                        }}
                        isLoading={isLoading}
                        emptyState={emptyState}
                        emptyStateCellClassName="px-6 py-16"
                        refs={{ scroll: scrollRef }}
                    />
                </div>
            </div>

            {/* Slide-over Panel */}
            {selectedPatient && (
                <ConversationPanel
                    patient={{
                        id: selectedPatient.patientId,
                        name: selectedPatient.name,
                        practiceName: selectedPatient.practiceName
                    }}
                    chatType={selectedPatient.status}
                    onChatTypeChange={handlePanelChatTypeChange}
                    onReadStateChange={refresh}
                    onClose={handleClosePanel}
                />
            )}
        </div>
    );
};

export default Conversations;
